import type { Properties } from "posthog-js";

/**
 * PostHog events for the three custom one-time-code flows (auth spec §8).
 *
 * I6 — no credential in telemetry: every payload is built field-by-field from
 * the typed arguments below, so an email, phone, or code value cannot reach an
 * event even if a caller passes one. Failure reasons come from the Clerk error
 * CODE, never its message.
 */
export const AUTH_CODE_EVENTS = {
  sent: "auth.code_sent",
  verified: "auth.code_verified",
  failed: "auth.code_failed",
  resent: "auth.code_resent",
} as const;

export type AuthCodeFlow = "funnel" | "readiness" | "settings_contact";
export type AuthCodeKind = "email" | "phone";
export type AuthCodeFailureReason = "wrong" | "expired" | "other";

type AuthCapture = (event: string, properties?: Properties) => void;

const WRONG_CODES = new Set(["form_code_incorrect", "incorrect_code"]);
const EXPIRED_CODES = new Set([
  "verification_expired",
  "form_code_expired",
  "code_expired",
]);

function errorCodes(error: unknown): string[] {
  if (!error || typeof error !== "object") return [];
  const record = error as { code?: unknown; errors?: unknown };
  const codes: string[] = [];
  if (typeof record.code === "string") codes.push(record.code);
  if (Array.isArray(record.errors)) {
    for (const item of record.errors) {
      const code = (item as { code?: unknown } | null)?.code;
      if (typeof code === "string") codes.push(code);
    }
  }
  return codes;
}

/** Map a Clerk verification error to the closed reason set, by code only. */
export function authCodeFailureReason(error: unknown): AuthCodeFailureReason {
  const codes = errorCodes(error);
  if (codes.some((code) => EXPIRED_CODES.has(code))) return "expired";
  if (codes.some((code) => WRONG_CODES.has(code))) return "wrong";
  return "other";
}

export function captureAuthCodeSent(
  capture: AuthCapture,
  flow: AuthCodeFlow,
  kind: AuthCodeKind,
) {
  capture(AUTH_CODE_EVENTS.sent, { flow, kind });
}

export function captureAuthCodeVerified(
  capture: AuthCapture,
  flow: AuthCodeFlow,
  attempt: number,
) {
  capture(AUTH_CODE_EVENTS.verified, { flow, attempt });
}

export function captureAuthCodeFailed(
  capture: AuthCapture,
  flow: AuthCodeFlow,
  attempt: number,
  reason: AuthCodeFailureReason,
) {
  capture(AUTH_CODE_EVENTS.failed, { flow, attempt, reason });
}

export function captureAuthCodeResent(capture: AuthCapture, flow: AuthCodeFlow) {
  capture(AUTH_CODE_EVENTS.resent, { flow });
}

/**
 * `auth.mfa_required_shown` — a protected action was refused for a missing
 * second factor and the UI that resolves it was shown. `remedy` is what the
 * UI offers: `setup` (enrol a factor) or `reverify` (retry / sign in again
 * with the factor already enrolled). Payload is exactly {app, remedy}.
 */
export const AUTH_MFA_REQUIRED_SHOWN_EVENT = "auth.mfa_required_shown";

export type AuthMfaRemedy = "setup" | "reverify";

export function captureAuthMfaRequiredShown(
  capture: AuthCapture,
  remedy: AuthMfaRemedy,
) {
  capture(AUTH_MFA_REQUIRED_SHOWN_EVENT, { app: "consumer", remedy });
}

/**
 * Passkey / session / MFA-enrolment events (auth spec §8). Payloads are built
 * field by field from closed unions — no passkey id, name, session id, email,
 * phone or code can reach an event (I6).
 */
export const AUTH_SESSION_STARTED_EVENT = "auth.session_started";
export const AUTH_PASSKEY_EVENTS = {
  prompted: "auth.passkey_enroll_prompted",
  enrolled: "auth.passkey_enrolled",
  declined: "auth.passkey_enroll_declined",
  removed: "auth.passkey_removed",
} as const;
export const AUTH_MFA_ENROLLED_EVENT = "auth.mfa_enrolled";

export type AuthSessionMethod = "passkey" | "email_code" | "unknown";
export type AuthPasskeyTrigger = "plaid_link" | "passport_publish" | "settings";
export type AuthPasskeyDeclineReason = "dismissed" | "cancelled" | "error";
export type AuthMfaFactor = "passkey" | "totp";

const PASSKEY_SIGN_IN_WINDOW_MS = 60_000;

function toMillis(value: unknown): number | null {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

/**
 * How this session began. `passkey` if a passkey was used within 60s of the
 * session being created; `email_code` if the token's factor-verification-age
 * shows a just-verified first factor and no second (`fva = [0, -1]`); else
 * `unknown` (the hosted sign-in exposes no event).
 */
export function deriveSessionMethod(input: {
  passkeys: ReadonlyArray<{ lastUsedAt?: Date | number | null }> | null | undefined;
  sessionCreatedAt: Date | number | null | undefined;
  fva: unknown;
}): AuthSessionMethod {
  const created = toMillis(input.sessionCreatedAt);
  if (created !== null) {
    for (const passkey of input.passkeys ?? []) {
      const used = toMillis(passkey?.lastUsedAt);
      if (used !== null && Math.abs(used - created) <= PASSKEY_SIGN_IN_WINDOW_MS) {
        return "passkey";
      }
    }
  }
  if (Array.isArray(input.fva) && input.fva[0] === 0 && input.fva[1] === -1) {
    return "email_code";
  }
  return "unknown";
}

export function captureAuthSessionStarted(
  capture: AuthCapture,
  method: AuthSessionMethod,
) {
  capture(AUTH_SESSION_STARTED_EVENT, { app: "consumer", method });
}

export function captureAuthPasskeyPrompted(
  capture: AuthCapture,
  trigger: AuthPasskeyTrigger,
) {
  capture(AUTH_PASSKEY_EVENTS.prompted, { trigger });
}

export function captureAuthPasskeyEnrolled(
  capture: AuthCapture,
  trigger: AuthPasskeyTrigger,
) {
  capture(AUTH_PASSKEY_EVENTS.enrolled, { trigger });
}

export function captureAuthPasskeyDeclined(
  capture: AuthCapture,
  trigger: AuthPasskeyTrigger,
  reason: AuthPasskeyDeclineReason,
) {
  capture(AUTH_PASSKEY_EVENTS.declined, { trigger, reason });
}

export function captureAuthPasskeyRemoved(capture: AuthCapture) {
  capture(AUTH_PASSKEY_EVENTS.removed, { app: "consumer" });
}

export function captureAuthMfaEnrolled(
  capture: AuthCapture,
  factor: AuthMfaFactor,
) {
  capture(AUTH_MFA_ENROLLED_EVENT, { app: "consumer", factor });
}
